import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState, } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Chip, Grid, IconButton, Stack, Typography, } from '@mui/material';
import { ThumbUp, ThumbDown, ThumbUpOutlined, ThumbDownOutlined, CloseRounded, } from '@mui/icons-material';
import { MuiMarkdown } from 'mui-markdown';
import { replaceStylesheetGlobals } from '../portal/theme/portalTheme';
// import VerticalMediaCard, { MediaCardConfig } from './VerticalMediaCard';
import { useConversationStore } from '../ConversationStoreProvider';
import { useTokens } from '../tokens/TokensProvider';
// import LoadingAnimation from './WaitingIndicator';
// import referencesToBatchKeys from './referencesToBatchKeys';
import PortalItemCard from './PortalItemCard';
import ProductCard from './product/ProductCard';
import WebPageCard from './WebPageCard';
import ReferencesSidebar from './ReferencesSidebar';
import DefaultProcessingIcon from './DefaultProcessingIcon';
import Image from './Image';
import useProductCardResponsive from './hooks/useProductCardResponsive';
import { getDualModeLinkSx } from './utils/linkStyles';
import InChatCarousel from './InChatCarousel';
import { DEFAULT_CAROUSEL_CARD_WIDTH } from './constants/dualModeConstants';
import dispatchCustomEvent from './utils/dispatchCustomEvent';
// Hidden message prefixes (system messages injected by client-side actions)
const HIDDEN_MESSAGE_PREFIXES = ['[ORDER_DATA_', '[ORDER_ERROR_'];
// Parse ProductAnswer markup from message content
function parseProductAnswerFromContent(content) {
    if (!content || typeof content !== 'string') {
        return { isProductAnswer: false };
    }
    // Match ProductAnswer markup with both single and double quotes
    const productAnswerRegex = /<ProductAnswer>[\s\S]*?<Product\s+key=["']([^"']+)["']>([^<]*)<\/Product>[\s\S]*?<Question>([\s\S]*?)<\/Question>[\s\S]*?<\/ProductAnswer>/i;
    const match = content.match(productAnswerRegex);
    if (!match) {
        return { isProductAnswer: false };
    }
    const result = {
        isProductAnswer: true,
        productKey: match[1],
        productName: match[2]?.trim(),
        question: match[3]?.trim() || '',
    };
    return result;
}
// Parse CategoryAnswer markup from message content
function parseCategoryAnswerFromContent(content) {
    if (!content || typeof content !== 'string') {
        return { isCategoryAnswer: false };
    }
    // Match CategoryAnswer markup with both single and double quotes
    const categoryAnswerRegex = /<CategoryAnswer>[\s\S]*?<Category\s+key=["']([^"']+)["']>([^<]*)<\/Category>[\s\S]*?<Question>([\s\S]*?)<\/Question>[\s\S]*?<\/CategoryAnswer>/i;
    const match = content.match(categoryAnswerRegex);
    if (!match) {
        return { isCategoryAnswer: false };
    }
    const result = {
        isCategoryAnswer: true,
        collectionKey: match[1],
        collectionName: match[2]?.trim(),
        question: match[3]?.trim() || '',
    };
    return result;
}
const MessageMetaRow = ({ label, alignRight }) => (_jsx(Box, { sx: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 0.5,
        alignSelf: alignRight ? 'flex-end' : 'flex-start',
    }, children: _jsx(Chip, { label: label, size: "small", variant: "outlined", sx: {
            fontSize: '0.75rem',
            fontWeight: 600,
            height: 22,
            '& .MuiChip-label': { px: 1 },
        } }) }));
MessageMetaRow.defaultProps = {
    alignRight: true,
};
const getResponsiveCardStyles = () => ({
    '& ul, & ol': {
        paddingLeft: 0,
        marginLeft: 0,
        listStyle: 'none',
    },
    '& li': {
        paddingLeft: 0,
        marginLeft: 0,
    },
    '& p': {
        marginLeft: 0,
        paddingLeft: 0,
    },
    '& .mc-itemlist': {
        // Flex row + gap — do not use all:unset/display:block; that wipes MUI Stack spacing.
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: '16px',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        marginLeft: 0,
        boxSizing: 'border-box',
        marginRight: 0,
        padding: '16px 0',
        font: 'inherit',
        color: 'inherit',
        listStyle: 'none',
    },
    '& li:has(.mc-itemlist)': {
        paddingLeft: 0,
        marginLeft: 0,
        listStylePosition: 'inside',
    },
    '& li:has(.mc-itemlist) > p': {
        marginLeft: 0,
        paddingLeft: 0,
    },
    '& .mc-itemlist:not(li .mc-itemlist):not(ol .mc-itemlist)': {
        paddingLeft: '24px',
    },
});
const markdownTypographySx = {
    marginTop: '0.25em',
    marginBottom: '0.25em',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
};
// const LinkComponent = (itemReferences) => (({ href, children }:any) => {
//   console.log('Link', { href, children });
//   if (href.startsWith('#')) {
//     const index = parseInt(href.substring(1), 10);
//     console.log('index', index, itemReferences.references[index]);
//     const reference = itemReferences.references[index];
//     return (
//       <ProductCard
//         title={reference.title}
//         image={reference.image}
//         url={reference.url}
//         width="25%"
//       />
//     );
//   }
//   return (
//     <a href={href} target="_top" rel="noreferrer">{children}</a>
//   );
// });
const UlComponent = ({ children, ...rest }) => {
    const hasContent = React.Children.toArray(children).some(Boolean);
    if (!hasContent)
        return null;
    return (_jsx("ul", { style: { marginLeft: '24px', marginTop: 0, marginBottom: '0.75em', padding: 0 }, ...rest, children: children }));
};
const LiComponent = ({ children, ...rest }) => {
    const flat = React.Children.toArray(children);
    const isEmpty = flat.length === 0
        || flat.every((c) => typeof c === 'string' && c.trim() === '');
    if (isEmpty)
        return null;
    return (_jsx("li", { style: { padding: '2px 0' }, ...rest, children: children }));
};
const Items = ({ children }) => (_jsx(Stack, { className: "mc-itemlist", direction: "row", spacing: 2, useFlexGap: true, sx: {
        py: 2,
        flexWrap: 'wrap',
        alignItems: 'stretch',
        columnGap: 2,
        rowGap: 2,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        '&:empty': { display: 'none' },
    }, children: children }));
const Carousel = ({ children }) => {
    // Extract carouselItems from CarouselItemReference children
    const carouselItems = React.Children.toArray(children)
        .filter((child) => (React.isValidElement(child) && typeof child.props?.itemKey === 'string'))
        .map((child) => {
        const { itemKey } = child.props;
        const { conversation } = child.props;
        if (!conversation?.itemReferences) {
            return null;
        }
        const reference = conversation.itemReferences.get(itemKey);
        if (!reference) {
            return null;
        }
        const item = {
            key: reference.key || itemKey,
            type: reference.type,
            target: reference.key || itemKey,
            title: reference.title,
            image: reference.image || reference.screenshot,
            description: reference.description,
            url: reference.url,
            id: reference.id,
            organizationID: reference.organizationID,
            itemID: reference.itemID,
            slug: reference.slug,
            price: reference.price,
            customizable: reference.customizable,
        };
        return item;
    })
        .filter((item) => item !== null);
    if (carouselItems.length === 0) {
        return null;
    }
    // Get config from first child's props (they should all have the same)
    const firstChild = React.Children.toArray(children).find((child) => React.isValidElement(child));
    const { mediaCardConfig: cardConfig = {}, openWebPagesInNewTab = false, onProductClick, onPortalRelativeNavigate, productFormatting, } = firstChild?.props || {};
    // Get carouselCardWidth from cardConfig if available
    const carouselCardWidth = cardConfig?.carouselCardWidth;
    return (_jsx(InChatCarousel, { carouselItems: carouselItems, cardConfig: {
            productFormatting,
            referenceFormatting: cardConfig?.referenceFormatting,
            clientAIConfig: cardConfig?.clientAIConfig,
        }, openWebPagesInNewTab: openWebPagesInNewTab, onProductClick: onProductClick, onPortalRelativeNavigate: onPortalRelativeNavigate, carouselCardWidth: carouselCardWidth }));
};
const CarouselItemReference = ({ itemKey, 
// These props are passed through to Carousel parent but not used directly here
// eslint-disable-next-line @typescript-eslint/no-unused-vars
conversation, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
mediaCardConfig, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
openWebPagesInNewTab, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
onProductClick, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
onPortalRelativeNavigate, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
productFormatting, }) => {
    // This component is used only to pass props to Carousel parent
    // The actual rendering is done by Carousel component
    // Return null as the visual rendering is handled by InChatCarousel
    // itemKey is used by Carousel parent to look up the reference
    if (!itemKey) {
        return null;
    }
    return null;
};
CarouselItemReference.defaultProps = {
    conversation: undefined,
    mediaCardConfig: undefined,
    openWebPagesInNewTab: false,
    onProductClick: undefined,
    onPortalRelativeNavigate: undefined,
    productFormatting: undefined,
};
const ProductLink = ({ itemKey, conversation, onProductClick, dualModeEnabled = false, }) => {
    if (!conversation?.itemReferences) {
        return null;
    }
    const reference = conversation.itemReferences.get(itemKey);
    if (!reference) {
        return null;
    }
    // ProductLink only works for products
    if (reference.type !== 'product') {
        return null;
    }
    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onProductClick && reference) {
            onProductClick(reference);
        }
    };
    return (_jsx(Box, { component: "a", href: reference.url, onClick: handleClick, sx: getDualModeLinkSx(dualModeEnabled), children: reference.title }));
};
ProductLink.defaultProps = {
    onProductClick: undefined,
    dualModeEnabled: false,
};
const MarkdownLink = ({ href, children, dualModeEnabled = false, }) => (_jsx(Box, { component: "a", href: href, target: "_top", rel: "noreferrer", sx: getDualModeLinkSx(dualModeEnabled), children: children }));
MarkdownLink.defaultProps = {
    dualModeEnabled: false,
};
const ItemReference = ({ itemKey, conversation, mediaCardConfig, openWebPagesInNewTab, onProductClick, onPortalRelativeNavigate, productFormatting, 
// Note: itemReferenceDisplay and dualModeEnabled are no longer used in ItemReference
// Link mode functionality has been moved to ProductLink component
// eslint-disable-next-line @typescript-eslint/no-unused-vars
itemReferenceDisplay = 'card', 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
dualModeEnabled = false, }) => {
    const { itemReferences } = conversation;
    const store = useConversationStore();
    const { referenceFormatting } = mediaCardConfig || {};
    // Responsive logic
    const { shouldApplyResponsiveStyles, isTinyScreen, cardWidth, } = useProductCardResponsive({ clientAIConfig: mediaCardConfig?.clientAIConfig });
    const finalCardWidth = useMemo(() => {
        if (dualModeEnabled) {
            return mediaCardConfig?.carouselCardWidth || DEFAULT_CAROUSEL_CARD_WIDTH;
        }
        return cardWidth;
    }, [dualModeEnabled, mediaCardConfig?.carouselCardWidth, cardWidth]);
    const computedReferenceStyle = useMemo(() => {
        if (!referenceFormatting || !referenceFormatting.outline)
            return {};
        return {
            borderStyle: referenceFormatting.outlineType || 'solid',
            borderColor: referenceFormatting.outlineColor || '#000',
            borderWidth: referenceFormatting.outlineWeight ?? 1,
            backgroundColor: referenceFormatting.backgroundColor || 'transparent',
        };
    }, [referenceFormatting]);
    const customCardStyle = useMemo(() => ({
        width: finalCardWidth,
        maxWidth: finalCardWidth,
    }), [finalCardWidth]);
    const combinedProductCardStyle = useMemo(() => ({
        ...computedReferenceStyle,
        ...customCardStyle,
    }), [computedReferenceStyle, customCardStyle]);
    if (!itemReferences) {
        return null;
    }
    const reference = itemReferences.get(itemKey);
    if (!reference) {
        return null;
    }
    const initialVariantId = store.customerContext?.currentPage?.product?.selectedVariantId;
    // Note: Link mode functionality has been moved to ProductLink component
    // ItemReference now only renders cards
    // Existing card rendering logic (unchanged)
    if (reference.type === 'product') {
        const shouldCenter = shouldApplyResponsiveStyles || isTinyScreen;
        return (_jsx(Box, { sx: {
                width: finalCardWidth,
                flexShrink: 0,
                display: 'flex',
                alignSelf: 'stretch',
                ...(shouldCenter && {
                    margin: '0 auto',
                }),
                '& > div': {
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                },
                // Equal-height row via flex column — avoid grid 1fr + overflow:hidden (clips titles).
                '& .MuiCard-root': {
                    flex: 1,
                    minHeight: 0,
                },
                '& .MuiCard-root > .MuiCardMedia-root': {
                    flexShrink: 0,
                },
                '& .MuiCardContent-root': {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible',
                    minHeight: 0,
                },
                '& .MuiCardContent-root > .MuiStack-root': {
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                },
                '& [data-card-slot="pricing"]': {
                    marginTop: 0,
                    marginBottom: 0,
                    lineHeight: 1,
                    flexShrink: 0,
                },
                '& [data-card-slot="pricing"] .MuiTypography-root': {
                    margin: 0,
                    lineHeight: 1.1,
                },
                '& [data-card-slot="actions"]': {
                    flexShrink: 0,
                },
            }, children: _jsx(ProductCard, { title: reference.title, description: reference.description, image: reference.image, price: reference.price, url: reference.url, customStyle: combinedProductCardStyle, onProductClick: onProductClick, product: reference, productFormatting: productFormatting, initialVariantId: initialVariantId }, itemKey) }));
    }
    if (reference.type === 'web') {
        return (_jsx(WebPageCard, { title: reference.title, image: reference.image || reference.screenshot, description: reference.description, url: reference.url, width: finalCardWidth, config: mediaCardConfig, openInNewTab: openWebPagesInNewTab, customStyle: computedReferenceStyle, onPortalRelativeNavigate: onPortalRelativeNavigate, itemType: reference.type, organizationID: reference.organizationID, entityId: reference.id, slug: reference.slug }, itemKey));
    }
    // console.log('reference', reference);
    return (_jsx(PortalItemCard, { title: reference.title, image: reference.image || reference.screenshot, description: reference.description, url: reference.url, width: finalCardWidth, config: mediaCardConfig, openInNewTab: openWebPagesInNewTab, customStyle: computedReferenceStyle, onProductClick: onProductClick, onPortalRelativeNavigate: onPortalRelativeNavigate, product: reference }, itemKey));
};
ItemReference.defaultProps = {
    mediaCardConfig: undefined,
    scrollRef: undefined,
    onProductClick: undefined,
    onPortalRelativeNavigate: undefined,
    productFormatting: undefined,
    itemReferenceDisplay: 'card',
    dualModeEnabled: false,
};
const RefinementComponent = ({ conversation, description, choices, selectedChoices, setSelectedChoices, messageConfig, }) => {
    if (!choices) {
        return null;
    }
    const { enableMultiRefinements, responseAlignment } = messageConfig;
    let parsedChoices;
    try {
        parsedChoices = choices.split(',').map((c) => c.trim());
    }
    catch (error) {
        console.log('error parsing choices', error);
        return null;
    }
    const toggleChoice = (choice) => {
        if (enableMultiRefinements) {
            setSelectedChoices((prevChoices) => (prevChoices.includes(choice)
                ? prevChoices.filter((c) => c !== choice)
                : [...prevChoices, choice]));
        }
        else if (messageConfig.onRefinementClick) {
            messageConfig.onRefinementClick(`${description}: ${choice}`);
        }
        else {
            conversation.sendPrompt(`${description}: ${choice}`);
        }
    };
    return (_jsxs(Box, { sx: { my: 2 }, children: [_jsx(Box, { sx: { fontStyle: 'italic' }, children: description }), _jsx(Stack, { direction: responseAlignment === 'right' ? 'row-reverse' : 'row', spacing: 1, sx: {
                    my: 1,
                    flexWrap: 'wrap',
                }, useFlexGap: true, children: parsedChoices.map((choice) => (_jsx(Chip, { label: choice, onClick: () => toggleChoice(choice), color: selectedChoices.includes(choice) ? 'primary' : 'default', variant: selectedChoices.includes(choice) ? 'filled' : 'outlined', className: "site-system-chip-primary" }, choice))) })] }));
};
const OpenSupportChat = ({ children }) => (_jsx(Button, { variant: "outlined", onClick: () => {
        window?.top?.postMessage({ source: 'microcommerce-ai-embed', type: 'openSupportChat' }, '*');
    }, children: children }));
const ProductDiscoveryMessage = ({ conversation }) => {
    React.useEffect(() => {
        conversation.sendTrackingEvent('product_discovery_message');
        // Only fire once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
};
const CustomerServiceMessage = ({ conversation, message, }) => {
    React.useEffect(() => {
        // Check if the message contains OpenSupport tool usage
        const hasOpenSupportTool = message.toolInvocations?.some((toolInvocation) => toolInvocation.toolName === 'openSupportChat');
        // Only send tracking event if OpenSupport tool is not used
        if (!hasOpenSupportTool) {
            conversation.sendTrackingEvent('customer_service_message');
        }
        // Only fire once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
};
const RefinementsEnd = ({ selectedChoices, setSelectedChoices, enableMultiRefinements, multiRefinementSubmitButtonText, conversation, }) => {
    if (!enableMultiRefinements || selectedChoices.length === 0)
        return null;
    return (_jsx(Box, { sx: { mt: 2 }, children: _jsx(Button, { className: "site-system-button-secondary", disabled: selectedChoices.length === 0, onClick: () => {
                const prompt = `Refinements: ${selectedChoices.join(', ')}`;
                conversation.sendPrompt(prompt);
                setSelectedChoices([]);
            }, children: multiRefinementSubmitButtonText }) }));
};
const SuggestedPrompt = ({ 
// type,
// key,
name, hideTitle, 
// conversation,
// message,
children, }) => (_jsxs(_Fragment, { children: [!hideTitle && _jsx(Typography, { variant: "overline", children: name }), _jsx(Box, { children: children })] }));
const Message = ({ message, conversation, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
messageConfig, mediaCardConfig, messageStyle, saveFeedback, isMobileView, hasCarouselItems, showProcessingIcon, processingIconImage, onProductClick, productFormatting, }) => {
    const store = useConversationStore();
    const { stylesheet } = store;
    const tokens = useTokens();
    const { shouldApplyResponsiveStyles } = useProductCardResponsive({
        clientAIConfig: mediaCardConfig?.clientAIConfig,
    });
    const [feedback, setFeedback] = useState(() => (message.feedback === 'up' || message.feedback === 'down'
        ? message.feedback
        : undefined));
    const [selectedChoices, setSelectedChoices] = useState([]);
    const hasExitToolInvocation = useMemo(() => (message.toolInvocations?.some((toolInvocation) => toolInvocation.toolName === 'exitConversation') || false), [message.toolInvocations]);
    const exitReadySentRef = useRef(false);
    const isAssistantMessage = message.role === 'assistant';
    let avatarPosition;
    if (isAssistantMessage) {
        if (messageConfig.responseAlignment === 'right') {
            avatarPosition = 'right';
        }
        else {
            avatarPosition = 'left';
        }
    }
    else if (messageConfig.questionAlignment === 'right' || messageConfig.questionAlignment === 'right-60') {
        avatarPosition = 'right';
    }
    else {
        avatarPosition = 'left';
    }
    const avatar = isAssistantMessage
        ? messageConfig.agentAvatarImage
        : messageConfig.userAvatarImage;
    // Define markdown options with extra margin if avatar is present
    const markdownOptions = useMemo(() => ({
        overrides: {
            // ...getOverrides(), // This will keep the other default overrides.
            table: {
                props: {
                    style: {
                        borderCollapse: 'collapse',
                        width: '100%',
                        marginTop: '16px',
                        marginBottom: '16px',
                    },
                },
            },
            th: {
                props: {
                    style: {
                        border: '1px solid #ccc',
                        padding: '8px',
                        backgroundColor: '#f5f5f5',
                        textAlign: 'left',
                    },
                },
            },
            td: {
                props: {
                    style: {
                        border: '1px solid #ccc',
                        padding: '8px',
                    },
                },
            },
            ol: {
                props: {
                    style: {
                        marginLeft: '24px',
                        marginTop: 0,
                        marginBottom: '0.75em',
                        padding: 0,
                    },
                },
            },
            ul: {
                component: UlComponent,
            },
            li: {
                component: LiComponent,
            },
            // Use div instead of <p> so block widgets (product cards, carousels) inside markdown are valid HTML.
            // markdown-to-jsx often wraps custom tags in a paragraph; <p> cannot contain <div> (validateDOMNesting).
            p: {
                component: ({ children, node: _node, className, ...rest }) => (_jsx(Typography, { component: "div", variant: "body1", className: ['mc-markdown-paragraph', className].filter(Boolean).join(' '), sx: markdownTypographySx, ...rest, children: children })),
            },
            pre: {
                props: {
                    style: {
                        margin: '16px 0',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        padding: '16px',
                        borderRadius: '6px',
                        overflowX: 'auto',
                        fontSize: '0.95em',
                    },
                },
            },
            code: {
                props: {
                    style: {
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                    },
                },
            },
            a: {
                component: MarkdownLink,
                allowAsProps: true,
                props: {
                    dualModeEnabled: messageConfig.dualModeEnabled,
                },
            },
            ProductLink: {
                component: ProductLink,
                allowAsProps: true,
                props: {
                    conversation,
                    onProductClick,
                    dualModeEnabled: messageConfig.dualModeEnabled,
                },
            },
            ItemReference: {
                component: ItemReference,
                allowAsProps: true,
                props: {
                    conversation,
                    mediaCardConfig,
                    openWebPagesInNewTab: messageConfig.openWebPagesInNewTab,
                    referenceFormatting: messageConfig.referenceFormatting,
                    onProductClick,
                    onPortalRelativeNavigate: messageConfig.onPortalRelativeNavigate,
                    productFormatting,
                    itemReferenceDisplay: messageConfig.itemReferenceDisplay,
                    dualModeEnabled: messageConfig.dualModeEnabled,
                },
            },
            ItemList: {
                component: Items,
            },
            Carousel: {
                component: Carousel,
                allowAsProps: true,
                props: {
                    conversation,
                    mediaCardConfig,
                    openWebPagesInNewTab: messageConfig.openWebPagesInNewTab,
                    onProductClick,
                    onPortalRelativeNavigate: messageConfig.onPortalRelativeNavigate,
                    productFormatting,
                },
            },
            CarouselItemReference: {
                component: CarouselItemReference,
                allowAsProps: true,
                props: {
                    conversation,
                    mediaCardConfig,
                    openWebPagesInNewTab: messageConfig.openWebPagesInNewTab,
                    onProductClick,
                    onPortalRelativeNavigate: messageConfig.onPortalRelativeNavigate,
                    productFormatting,
                },
            },
            Refinement: {
                component: RefinementComponent,
                allowAsProps: true,
                props: {
                    conversation,
                    selectedChoices,
                    setSelectedChoices,
                    messageConfig,
                },
            },
            OpenSupportChat: {
                component: OpenSupportChat,
            },
            RefinementsEnd: {
                component: RefinementsEnd,
                allowAsProps: true,
                props: {
                    conversation,
                    enableMultiRefinements: messageConfig?.enableMultiRefinements,
                    multiRefinementSubmitButtonText: messageConfig?.multiRefinementSubmitButtonText || tokens.getValue('submitRefinementsLabel'),
                    selectedChoices,
                    setSelectedChoices,
                },
            },
            ProductDiscovery: {
                component: ProductDiscoveryMessage,
                props: {
                    conversation,
                },
            },
            CustomerService: {
                component: CustomerServiceMessage,
                props: {
                    conversation,
                    message,
                },
            },
            SuggestedPrompt: {
                component: SuggestedPrompt,
                props: {
                    conversation,
                    message,
                    hideTitle: messageConfig?.hideProductAnswerTitleInMessage ?? false,
                },
            },
        },
    }), [
        conversation,
        mediaCardConfig,
        messageConfig.dualModeEnabled,
        messageConfig.enableMultiRefinements,
        messageConfig.hideProductAnswerTitleInMessage,
        messageConfig.itemReferenceDisplay,
        messageConfig.multiRefinementSubmitButtonText,
        messageConfig.onPortalRelativeNavigate,
        messageConfig.openWebPagesInNewTab,
        messageConfig.referenceFormatting,
        selectedChoices,
        message,
        onProductClick,
        productFormatting,
        tokens,
    ]);
    // Parse ProductAnswer markup for user messages
    const productAnswerData = useMemo(() => {
        if (message.role === 'user') {
            return parseProductAnswerFromContent(message.content);
        }
        return { isProductAnswer: false };
    }, [message.role, message.content]);
    const categoryAnswerData = useMemo(() => {
        if (message.role === 'user') {
            return parseCategoryAnswerFromContent(message.content);
        }
        return { isCategoryAnswer: false };
    }, [message.role, message.content]);
    const messageMeta = useMemo(() => {
        const isUser = message.role === 'user';
        const isProductQ = isUser && productAnswerData.isProductAnswer;
        const isCategoryQ = isUser && categoryAnswerData.isCategoryAnswer;
        let label;
        if (isProductQ) {
            label = 'Product question';
        }
        else if (isCategoryQ) {
            label = 'Category question';
        }
        return {
            isProductQuestion: isProductQ,
            isCategoryQuestion: isCategoryQ,
            label,
        };
    }, [message.role, productAnswerData.isProductAnswer, categoryAnswerData.isCategoryAnswer]);
    const messageContent = useMemo(() => {
        let content;
        // For assistant messages, always use content as-is to preserve Carousel markup
        // This ensures Carousel in dual search mode is never affected by
        // ProductAnswer/CategoryAnswer parsing
        if (message.role === 'assistant') {
            content = message.content;
        }
        else if (productAnswerData.isProductAnswer && productAnswerData.question) {
            // Only process ProductAnswer for user messages
            content = productAnswerData.question;
            // Still process refinements if present
            if (messageConfig?.enableMultiRefinements
                && productAnswerData.question.includes('<Refinement')
                && !productAnswerData.question.includes('<RefinementsEnd>')) {
                const refinementRegex = /<Refinement[\s\S]*?\/>/g;
                const matches = productAnswerData.question.match(refinementRegex);
                if (matches && matches.length > 0) {
                    const lastMatch = matches[matches.length - 1];
                    content = productAnswerData.question.replace(lastMatch, `${lastMatch}\n<RefinementsEnd />`);
                }
            }
        }
        else if (categoryAnswerData.isCategoryAnswer && categoryAnswerData.question) {
            // Only process CategoryAnswer for user messages
            content = categoryAnswerData.question;
            // Still process refinements if present
            if (messageConfig?.enableMultiRefinements
                && categoryAnswerData.question.includes('<Refinement')
                && !categoryAnswerData.question.includes('<RefinementsEnd>')) {
                const refinementRegex = /<Refinement[\s\S]*?\/>/g;
                const matches = categoryAnswerData.question.match(refinementRegex);
                if (matches && matches.length > 0) {
                    const lastMatch = matches[matches.length - 1];
                    content = categoryAnswerData.question.replace(lastMatch, `${lastMatch}\n<RefinementsEnd />`);
                }
            }
        }
        else {
            // Original logic for non-ProductAnswer user messages
            content = message.content;
            if (messageConfig?.enableMultiRefinements
                && message.content?.includes('<Refinement')
                && !message.content?.includes('<RefinementsEnd>')) {
                const refinementRegex = /<Refinement[\s\S]*?\/>/g;
                const matches = message.content.match(refinementRegex);
                if (matches && matches.length > 0) {
                    const lastMatch = matches[matches.length - 1];
                    content = message.content.replace(lastMatch, `${lastMatch}\n<RefinementsEnd />`);
                }
            }
        }
        // Fix missing space after punctuation before capital letter.
        // Applied at render-time (not in the model) so the stored content matches the
        // raw stream and dedup comparisons stay correct — avoids layout flicker.
        if (content) {
            content = content.replace(/([.!?])([A-Z])/g, '$1 $2');
        }
        return content;
    }, [
        message.content,
        message.role,
        messageConfig.enableMultiRefinements,
        productAnswerData,
        categoryAnswerData,
    ]);
    useEffect(() => {
        if (!hasExitToolInvocation || exitReadySentRef.current) {
            return;
        }
        const messageHasContent = Boolean(typeof message.content === 'string' && message.content.trim().length > 0);
        if (!messageHasContent) {
            return;
        }
        const renderTimestamp = Date.now();
        let messageCreatedAtISO;
        try {
            const createdAtValue = message.createdAt;
            if (createdAtValue) {
                const createdAtDate = createdAtValue instanceof Date
                    ? createdAtValue : new Date(createdAtValue);
                if (!Number.isNaN(createdAtDate.getTime())) {
                    messageCreatedAtISO = createdAtDate.toISOString();
                }
            }
        }
        catch (error) {
            console.warn('[ConversationExit] unable to parse message.createdAt', { error, createdAt: message.createdAt });
        }
        const exitToolInvocation = message.toolInvocations?.find((toolInvocation) => toolInvocation.toolName === 'exitConversation');
        const exitReason = exitToolInvocation?.args?.reason
            || exitToolInvocation?.result?.reason
            || 'user_requested';
        const detail = {
            messageId: message.id,
            conversationId: conversation.id,
            messageCreatedAt: messageCreatedAtISO,
            renderTimestamp,
            renderTimestampISO: new Date(renderTimestamp).toISOString(),
            reason: exitReason,
            hasContent: messageHasContent,
        };
        dispatchCustomEvent('microcommerce:conversationExitReady', detail);
        exitReadySentRef.current = true;
    }, [
        conversation.id,
        exitReadySentRef,
        hasExitToolInvocation,
        message.content,
        message.createdAt,
        message.id,
        message.toolInvocations,
    ]);
    // Note: Carousel is now handled as an explicit component in markdownOptions
    // No need to detect markers or check carouselItems separately
    const handleFeedback = (type) => {
        // Toggle feedback or reset it to undefined
        const newFeedback = feedback === type ? undefined : type;
        setFeedback(newFeedback);
        message.setFeedback(newFeedback);
        if (newFeedback) {
            saveFeedback(newFeedback, message.id, conversation.id);
        }
    };
    // if (!message || !(message.content || message.toolInvocations)) {
    //   return null;
    // }
    // Hide system messages (client-side injected data)
    if (message?.content) {
        const content = String(message.content);
        if (HIDDEN_MESSAGE_PREFIXES.some((prefix) => content.startsWith(prefix))) {
            return null;
        }
    }
    const shouldShowReferenceItems = Boolean(messageConfig.showItemReferences
        && hasCarouselItems
        && messageConfig.maxItemsInRightRail > 0
        && (!isMobileView || (isMobileView && !messageConfig.hideRightRailOnMobile)));
    let flexDirection = 'row';
    let isRightAligned = false;
    if (avatar) {
        if (avatarPosition === 'right') {
            flexDirection = 'row-reverse';
            isRightAligned = true;
        }
        else {
            flexDirection = 'row';
        }
    }
    else {
        flexDirection = 'row';
        // Consider questionAlignment when no avatar
        if (!isAssistantMessage && (messageConfig.questionAlignment === 'right' || messageConfig.questionAlignment === 'right-60')) {
            isRightAligned = true;
        }
    }
    return (_jsxs(Grid, { container: true, spacing: 2, sx: {
            // paddingX: { xs: '40px', sm: '48px' },
            marginY: '0px',
        }, children: [_jsx(Grid, { item: true, xs: 12, md: shouldShowReferenceItems ? 8 : 12, children: _jsxs(Box, { sx: {
                        display: 'flex',
                        flexDirection,
                        alignItems: 'flex-start',
                    }, children: [avatar && (_jsx(Box, { sx: {
                                flexShrink: 0,
                                marginLeft: isAssistantMessage && messageConfig.responseAlignment === 'leftIndent' ? '28px' : 1,
                                marginRight: 1,
                            }, children: _jsx(Image, { url: avatar.url, alt: avatar.alt, status: avatar.status, avatar: true, avatarVariant: "circular", customStyle: {
                                    height: '32px',
                                    width: '32px',
                                } }) })), _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                                width: '100%',
                                alignSelf: isRightAligned ? 'flex-end' : 'flex-start',
                                alignItems: isRightAligned ? 'flex-end' : 'flex-start',
                            }, children: [(messageMeta.isProductQuestion || messageMeta.isCategoryQuestion)
                                    && messageMeta.label
                                    && (_jsx(MessageMetaRow, { label: messageMeta.label, alignRight: isRightAligned })), _jsxs(Box, { className: "mc-markdown", sx: {
                                        ...messageStyle,
                                        '--mc-list-indent': '24px',
                                        color: messageStyle?.color || 'inherit',
                                        minWidth: 0,
                                        maxWidth: '100%',
                                        overflowWrap: 'anywhere',
                                        '& .MuiTypography-root': markdownTypographySx,
                                        '& p, & li, & td, & th, & a': {
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                            lineHeight: 'inherit',
                                            color: 'inherit',
                                        },
                                        '& .mc-markdown-paragraph': {
                                            marginBottom: 'var(--mc-paragraph-spacing, 0.25em)',
                                        },
                                        // Loose-list fix: when markdown-to-jsx wraps a <li>'s text in a <p>
                                        // (triggered by blank lines between bullets), our p→div override makes
                                        // it block-level and stretches to full bubble width. Inline collapses it
                                        // back to text width, matching tight-list items visually.
                                        '& li .mc-markdown-paragraph': {
                                            display: 'inline',
                                            marginTop: 0,
                                            marginBottom: 0,
                                        },
                                        // Prevent nested <ul> (sub-bullets) from adding bottom margin that
                                        // creates a gap before the next sibling bullet.
                                        '& li > ul': {
                                            marginBottom: 0,
                                        },
                                        ...(shouldApplyResponsiveStyles && getResponsiveCardStyles()),
                                    }, children: [_jsx(MuiMarkdown, { options: markdownOptions, children: messageContent }), message.toolInvocations?.map((toolInvocation) => {
                                            // const { toolCallId } = toolInvocation;
                                            // const addResult = (result: string) => {
                                            //   addToolResult({ toolCallId, result });
                                            // };
                                            // // render confirmation tool (client-side tool with user interaction)
                                            // if (toolInvocation.toolName === 'askForConfirmation') {
                                            //   return (
                                            //     <div key={toolCallId}>
                                            //       {toolInvocation.args.message}
                                            //       <div>
                                            //         {'result' in toolInvocation ? (
                                            //           <b>{toolInvocation.result}</b>
                                            //         ) : (
                                            //           <>
                                            //             <Button onClick={() => addResult('Yes')}>Yes</Button>
                                            //             <Button onClick={() => addResult('No')}>No</Button>
                                            //           </>
                                            //         )}
                                            //       </div>
                                            //     </div>
                                            //   );
                                            // }
                                            if (toolInvocation.toolName === 'selectionWizard') {
                                                return null;
                                            }
                                            if (toolInvocation.toolName === 'openSupportChat') {
                                                try {
                                                    if ('result' in toolInvocation && toolInvocation.result) {
                                                        const { supportCenterOpen, triggeredDate } = toolInvocation.result;
                                                        const now = Date.now();
                                                        if (now - triggeredDate < 5000) {
                                                            // wait 3 seconds before opening chat
                                                            console.log('waiting before opening chat', messageConfig.openSupportToolDelay);
                                                            conversation.sendTrackingEvent('open_support_chat', {
                                                                support_center_open: supportCenterOpen,
                                                            });
                                                            setTimeout(() => {
                                                                console.log('openSupportChat message posted');
                                                                window?.top?.postMessage({
                                                                    source: 'microcommerce-ai-embed',
                                                                    type: 'openSupportChat',
                                                                    supportCenterOpen,
                                                                }, '*');
                                                            }, messageConfig.openSupportToolDelay || 4000);
                                                        }
                                                        else {
                                                            console.log('Triggered more than 5 seconds ago, skipping.');
                                                        }
                                                        return (_jsx("div", { children: messageConfig.openSupportToolMessage }));
                                                    }
                                                    return (_jsx("div", { children: messageConfig.openSupportToolMessage }));
                                                }
                                                catch (error) {
                                                    console.error('error opening support chat', error);
                                                }
                                            }
                                            // by default dont show anything
                                            return null;
                                        }), isAssistantMessage && (messageConfig.showFeedbackButtons || feedback || messageConfig.showCloseButton) && (_jsxs(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", sx: { marginTop: 1 }, children: [_jsx(Stack, { direction: "row", spacing: 1, children: (messageConfig.showFeedbackButtons || feedback) && (_jsxs(_Fragment, { children: [_jsx(IconButton, { onClick: () => {
                                                                    if (!messageConfig.showFeedbackButtons)
                                                                        return;
                                                                    handleFeedback('up');
                                                                }, size: "small", sx: {
                                                                    color: replaceStylesheetGlobals(stylesheet, messageConfig.feedbackButton?.color || '#000'),
                                                                    '&:hover': {
                                                                        color: replaceStylesheetGlobals(stylesheet, messageConfig.feedbackButton?.hover || messageConfig.feedbackButton?.color || '#ccc'),
                                                                    },
                                                                }, children: feedback === 'up' ? _jsx(ThumbUp, {}) : _jsx(ThumbUpOutlined, {}) }), _jsx(IconButton, { onClick: () => {
                                                                    if (!messageConfig.showFeedbackButtons)
                                                                        return;
                                                                    handleFeedback('down');
                                                                }, size: "small", sx: {
                                                                    color: replaceStylesheetGlobals(stylesheet, messageConfig.feedbackButton?.color || '#000'),
                                                                    '&:hover': {
                                                                        color: replaceStylesheetGlobals(stylesheet, messageConfig.feedbackButton?.hover || messageConfig.feedbackButton?.color || '#ccc'),
                                                                    },
                                                                }, children: feedback === 'down' ? _jsx(ThumbDown, {}) : _jsx(ThumbDownOutlined, {}) })] })) }), messageConfig.showCloseButton && (_jsx(IconButton, { "aria-label": "Close answer", onClick: messageConfig.onClose, size: "small", sx: {
                                                        color: replaceStylesheetGlobals(stylesheet, messageConfig.feedbackButton?.color || '#000'),
                                                        '&:hover': {
                                                            color: replaceStylesheetGlobals(stylesheet, messageConfig.feedbackButton?.hover || messageConfig.feedbackButton?.color || '#ccc'),
                                                        },
                                                    }, children: _jsx(CloseRounded, {}) }))] })), showProcessingIcon && (_jsx(Box, { sx: { mb: 2 }, children: processingIconImage ? (_jsx(Image, { url: processingIconImage.url, alt: processingIconImage.alt, status: processingIconImage.status, customStyle: {
                                                    objectFit: 'contain',
                                                    maxHeight: '120px',
                                                } })) : (_jsx(DefaultProcessingIcon, { color: replaceStylesheetGlobals(stylesheet, messageConfig?.defaultProcessingIcon?.color || 'color_primary'), width: messageConfig?.defaultProcessingIcon?.width, height: messageConfig?.defaultProcessingIcon?.height })) }))] })] })] }) }), shouldShowReferenceItems && (_jsx(Grid, { item: true, xs: 12, md: 4, children: _jsx(ReferencesSidebar, { message: message, cardConfig: mediaCardConfig, openWebPagesInNewTab: messageConfig.openWebPagesInNewTab, onPortalRelativeNavigate: messageConfig.onPortalRelativeNavigate, isMobileView: isMobileView, maxItems: messageConfig.maxItemsInRightRail, onProductClick: onProductClick, dualModeEnabled: messageConfig.dualModeEnabled }) }))] }));
};
Message.defaultProps = {
    processingIconImage: undefined,
    onProductClick: undefined,
    productFormatting: undefined,
};
export default observer(Message);
//# sourceMappingURL=Message.js.map